import * as Sentry from '@sentry/react';

import { isSentryDeliveryEnabled } from './sentry-delivery-gate';

/**
 * The user-initiated half of PSTUDIO-4194 (criteria A-E): what "Report a bug"
 * sends, and what the user is given to quote to support.
 *
 * It deliberately does NOT go through `reportError`. That function exists to
 * report a FAILURE the app caught, and its whole behaviour — grouping by the
 * message with a support id stripped out, `captureException` on an `Error` —
 * is about a stack we did not ask for. A bug report has no stack, no exception
 * and no natural grouping: two users describing the same screen in different
 * words are two reports, and one user reporting twice is two reports as well.
 *
 * Nothing here writes to our own database. That was decided on 2026-09-14 and
 * is a separate ticket; this module is the Sentry channel only.
 */

/** Names the channel on every event, so a report is searchable apart from crashes. */
export const USER_BUG_REPORT_SOURCE = 'user-bug-report';

/**
 * How many screenshots the form takes.
 *
 * Not a Sentry limit — a product one. The budget below is what Sentry enforces;
 * this keeps the form from becoming an upload tool.
 */
export const MAX_SCREENSHOTS = 5;

/** The largest single screenshot, well inside the per-request budget below. */
export const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;

/**
 * The largest a report's screenshots may be, taken together.
 *
 * Sentry accepts "at most 40MB for a compressed request" and rejects anything
 * above it with `413 Payload Too Large`, dropping the data immediately
 * (https://docs.sentry.io/platforms/javascript/enriching-events/attachments/,
 * read 2026-09-20). A PNG screenshot barely compresses, so the raw total is
 * what has to fit — half the documented ceiling leaves room for the event
 * itself and for whatever the SDK attaches on its own.
 */
export const MAX_TOTAL_SCREENSHOT_BYTES = 20 * 1024 * 1024;

const TITLE_PREFIX = 'User bug report';

/** A uuid where the platform has one, and something unique where it does not. */
const newId = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * What a feedback card's screenshot has to be called.
 *
 * Taken from Sentry's own widget rather than from the docs, which do not state
 * a requirement: `@sentry/feedback` builds its attachment as
 * `{ data, filename: 'screenshot.png', contentType: 'application/png' }` and
 * hands it to `captureFeedback` through the hint.
 */
const SCREENSHOT_ATTACHMENT_NAME = 'screenshot.png';

/**
 * How the id is written into the feedback's own text.
 *
 * Support pastes the id it was given, unprefixed, into whatever screen is
 * open. Measured on 2026-09-20 against the real project: User Feedback's free
 * text searches the MESSAGE — `"Third verification run"` finds the card —
 * while a bare id matches nothing there, because a tag is only searched when
 * its key is named (`errorId:<id>`). Issues, by contrast, redirects on a bare
 * event id. Putting the id in the text is what makes the same paste work in
 * both places.
 */
const buildFeedbackMessage = (message: string, errorId: string): string => {
  const written = message.trim();

  return written
    ? `${written}\n\nReport ID: ${errorId}`
    : `Screenshot-only report, no message.\n\nReport ID: ${errorId}`;
};

/**
 * How much of the user's first line reaches the title.
 *
 * Long enough to tell two reports apart in the issue list, short enough that
 * the list stays readable. The whole message travels in `extra.message`, so
 * nothing the user wrote is lost to this bound.
 */
const MAX_TITLE_QUOTE_LENGTH = 120;

/**
 * What the report is called in Sentry.
 *
 * The ticket asks for a title that states this came from a user rather than
 * from a crash, so the prefix is fixed and the user's own first line is what
 * distinguishes one report from the next. Only the first line: a user who
 * pastes a stack trace or a long paragraph would otherwise put all of it in
 * the issue list.
 */
export const buildUserBugReportTitle = (message: string): string => {
  const [firstLine = ''] = message.split('\n');
  const quote = firstLine.trim();

  if (!quote) {
    return TITLE_PREFIX;
  }

  const shortened =
    quote.length > MAX_TITLE_QUOTE_LENGTH
      ? `${quote.slice(0, MAX_TITLE_QUOTE_LENGTH)}…`
      : quote;

  return `${TITLE_PREFIX}: ${shortened}`;
};

export type ScreenshotRejectionReason =
  | 'not-an-image'
  | 'too-large'
  | 'too-many'
  | 'over-total-size';

export interface RejectedScreenshot {
  file: File;
  reason: ScreenshotRejectionReason;
}

export interface ScreenshotSelection {
  accepted: File[];
  rejected: RejectedScreenshot[];
}

/**
 * Decides which of the picked files can travel, and says why the rest cannot.
 *
 * The refusal is the point: an oversized request is dropped by Sentry with a
 * 413 and never becomes a report, so a form that accepted everything would
 * show the user a success screen for a report nobody received. Every file is
 * given a verdict, and the caller renders the reasons.
 */
export const selectBugReportScreenshots = (
  files: File[]
): ScreenshotSelection => {
  const accepted: File[] = [];
  const rejected: RejectedScreenshot[] = [];
  let total = 0;

  files.forEach((file) => {
    if (!file.type.startsWith('image/')) {
      rejected.push({ file, reason: 'not-an-image' });
      return;
    }

    if (file.size > MAX_SCREENSHOT_BYTES) {
      rejected.push({ file, reason: 'too-large' });
      return;
    }

    if (accepted.length >= MAX_SCREENSHOTS) {
      rejected.push({ file, reason: 'too-many' });
      return;
    }

    if (total + file.size > MAX_TOTAL_SCREENSHOT_BYTES) {
      rejected.push({ file, reason: 'over-total-size' });
      return;
    }

    accepted.push(file);
    total += file.size;
  });

  return { accepted, rejected };
};

/**
 * The search to hand support, rather than a bare id.
 *
 * Measured on 2026-09-20 in the real UI: the User Feedback page searches by
 * TAG, so a bare id matches nothing there, while Issues redirects on a bare
 * event id. `errorId:<id>` is the one form that works in both, which is why it
 * — and not the id alone — is what the copy button puts on the clipboard.
 */
export const buildSupportSearchQuery = (errorId: string): string =>
  `errorId:${errorId}`;

/**
 * Whether a report sent right now would actually leave the browser.
 *
 * `scope.captureEvent` RETURNS an event id and silently drops the event when
 * no client is configured (`@sentry/core` `scope.js`: "No client configured on
 * scope - will not capture event!"), so nothing downstream can tell a
 * delivered report from a discarded one. Measured on 2026-09-20 with Sentry
 * off: the form showed its success screen and an id while the network carried
 * zero envelopes.
 *
 * Which deploys have a client is decided in LaunchDarkly (`sentry-gate.ts`),
 * so on a deploy whose flag is off this is the NORMAL state — the button is
 * hidden there rather than lying about what it did.
 *
 * BOTH halves are asked, because there are two ways for a report to go
 * nowhere and only one of them is the missing client. Throwing the kill switch
 * on a running tab leaves `getClient()` truthy while every event is dropped by
 * the delivery-gate processor; with only the first half the form confirmed a
 * report whose issue was discarded, and left a feedback card pointing at an
 * event id that does not exist. That is the same promise
 * `e5a3c4f6ac` ("never confirm a bug report that was never sent") was written
 * to keep, one layer further in.
 */
export const isErrorReportingActive = (): boolean =>
  Boolean(Sentry.getClient()) && isSentryDeliveryEnabled();

export interface UserBugReportInput {
  /** What the user wrote. May be empty when they only attached screenshots. */
  message: string;
  /** Which app the report came from, matching the tag `reportError` sets. */
  app: string;
  /** Already filtered by `selectBugReportScreenshots`. */
  screenshots?: File[];
  /** The page the user was on — the one piece of context they cannot type. */
  url?: string;
  /** Who to come back to, when the session knows. */
  user?: { email?: string; name?: string };
}

export interface UserBugReportResult {
  /** The Sentry event id, which is what the user quotes to support. */
  errorId: string;
}

/**
 * Strips any path component a browser might put in a picked file's name.
 *
 * Sentry's attachment contract is "the name of the uploaded file without a path
 * component" (`Attachment.filename`).
 */
const toAttachmentFilename = (name: string): string =>
  name.split(/[\\/]/).pop() || 'screenshot';

/**
 * Sends the report and returns the id to show the user.
 *
 * TWO events, on purpose, because the ticket asks for two different things.
 * `captureMessage` is what produces an ISSUE with a title that names the
 * channel — which is what support searches, and what every screenshot hangs
 * off. `captureFeedback` is what puts the same words in Sentry's own User
 * Feedback list, linked to that issue through `associatedEventId`.
 *
 * The FIRST screenshot is sent twice, and that is deliberate: a User Feedback
 * card renders a screenshot only when the FEEDBACK event carries it, which is
 * how Sentry's own widget does it — it attaches the image to the feedback with
 * the filename `screenshot.png` (`@sentry/feedback`, the `screenshotInput`
 * value passed as `{ attachments }` to `captureFeedback`). Without that copy
 * the card shows text only and the image is one click away on the linked
 * issue. Only the first one is copied: the widget sends exactly one, and the
 * rest would double a payload already budgeted against a 40MB request.
 */
export const submitUserBugReport = async ({
  message,
  app,
  screenshots = [],
  url,
  user,
}: UserBugReportInput): Promise<UserBugReportResult> => {
  // The second line behind hiding the button: without a client the SDK would
  // hand back an id for a report it never sent, and the user would be shown a
  // support id that matches nothing.
  if (!isErrorReportingActive()) {
    throw new Error('Sentry has no client; the report would be discarded');
  }

  const attachments = await Promise.all(
    screenshots.map(async (file) => ({
      filename: toAttachmentFilename(file.name),
      contentType: file.type || 'application/octet-stream',
      data: new Uint8Array(await file.arrayBuffer()),
    }))
  );

  // What keeps two reports from becoming one issue. Sentry groups a message
  // event by its text, so two users opening with the same sentence — or one
  // user reporting the same screen twice — would arrive as repeats of a single
  // issue, and the second report would be a "+1 event" nobody triages.
  const reportId = newId();

  // The event id is OURS rather than the SDK's, and that is what lets it be a
  // TAG on this very event: `prepareEvent` takes `event.event_id ||
  // hint.event_id || uuid4()`, so a value passed in the hint becomes the id.
  // Without it the id only exists after the event is sent, too late to tag it
  // — and an untagged issue cannot be found by the search the user is handed.
  const errorId = newId().replace(/-/g, '');

  Sentry.withScope((scope) => {
    attachments.forEach((attachment) => scope.addAttachment(attachment));

    scope.setLevel('info');
    scope.setFingerprint([USER_BUG_REPORT_SOURCE, reportId]);
    // `reportId` is a TAG, not extra: Sentry's search does not look inside
    // `extra`, so a report filed there could not be found by it — measured on
    // 2026-09-20, `reportId:<uuid>` returned nothing for the issue while
    // finding the feedback, which carries it as a tag.
    scope.setTags({ app, source: USER_BUG_REPORT_SOURCE, reportId, errorId });
    scope.setExtras({
      // The title quotes the first line only; this is everything they wrote.
      message,
      ...(url ? { url } : {}),
      screenshotCount: attachments.length,
    });

    Sentry.captureEvent(
      { message: buildUserBugReportTitle(message), level: 'info' },
      { event_id: errorId }
    );
  });

  const [firstScreenshot] = attachments;

  try {
    Sentry.captureFeedback(
      {
        message: buildFeedbackMessage(message, errorId),
        associatedEventId: errorId,
        source: USER_BUG_REPORT_SOURCE,
        ...(url ? { url } : {}),
        ...(user?.email ? { email: user.email } : {}),
        ...(user?.name ? { name: user.name } : {}),
        // `errorId` repeats what Sentry stores as `associated_event_id`, and
        // it is here because that field is only searchable together with
        // `issue.category:feedback`. This is the id the user was handed, so it
        // is the one support will paste — and with both tags present, either
        // id finds either record.
        tags: { app, reportId, errorId },
      },
      firstScreenshot
        ? {
            // The name is the widget's, not ours: it is what Sentry's own
            // feedback UI puts in the card.
            attachments: [
              { ...firstScreenshot, filename: SCREENSHOT_ATTACHMENT_NAME },
            ],
          }
        : undefined
    );
  } catch {
    // Best-effort. The issue is already filed and the user is about to be
    // shown its id; losing the feedback-list copy must not take that away.
  }

  return { errorId };
};
